import { HttpClient } from "@angular/common/http";
import { LacrosseEntity } from "../lacrosse.entity";
import { FirebaseAuthService } from "../../noizu/services/firebase-auth.service";
import { WidgetEnum } from "../../enums";

export class UserDeviceAssociationEntity extends LacrosseEntity {
  public status = null;
  public batterySettings = null;
  public expanded = false;
  public editMode = false;
  public saving = false;
  public checking = false;
  private _createPromise: any = null;
  public isStation = false;
  public sensor_serial = "";
  public locationName = "";
  public location_id = 0;
  public device_name = "Default";
  public model_number = "";
  public type_name = "";
  public sensor_identifier = "";
  public geo = { anonymous: false, pc: "", timezone: "", countryCode: "US" };
  public showReloc = false;
  public newPos = null;
  public pos = 0;
  public last = false;
  public isfact23 = false;
  public weight = 0;
  public _sref_module = "user-device";

  public editBackup = {};

  constructor(client: HttpClient, auth: FirebaseAuthService, json) {
    super(client, auth, json);
    this.reload(json);
    this.weight = json.weight != null ? json.weight : 0;
  }

  reload(json) {
    if (json.name) {
      this.device_name = json.name;
    }
    if (json.sensor) {
      this.model_number = json.sensor.type.internalName;
      this.sensor_serial = json.sensor.serial;
      if (
        json.sensor &&
        json.sensor.attributes &&
        json.sensor.attributes.display == "1"
      )
        this.isStation = true;
      if (
        json.sensor &&
        json.sensor.attributes &&
        json.sensor.attributes.factory == "23"
      )
        this.isfact23 = true;
      if (json.sensor.geo && this.isfact23) {
        this.geo.pc = json.sensor.geo.zip;
        this.geo.timezone = json.sensor.geo.timezone;
        this.geo.anonymous = json.sensor.geo.anonymous;
        this.geo.countryCode = json.sensor.geo.countryCode;
      } else {
        if (this.isfact23) {
          this.geo = null;
        }
      }
      this.model_number = json.sensor.type.internalName;
      this.type_name = json.sensor.type.name;
      this.sensor_identifier = json.sensor.id;
    }
  }

  getGeo() {
    return this._get(
      `${this.ingvEndpoint()}/forecast/admin/id/${
        this.sensor_serial
      }?details=false`,
      (data, resolve) => {
        if (data.zip) {
          this.geo.pc = data.zip;
        } else if (data.city) {
          this.geo.pc = data.city;
        } else {
          this.geo.anonymous = true;
        }
        this.geo["called"] = true;
        resolve(data);
      },
      {}
    ).catch((e) => {
      console.log(e);
    });
  }

  getStatusPromise() {
    return this._get(
      `${this.ingvEndpoint()}/active-user/device-association/ref.device.${
        this.sensor_serial
      }@${this.raw.sensor.series}/status?tz=America/Chicago`,
      (data, resolve) => {
        this.status = { lastUpdate: data.last_update };
        resolve(true);
      },
      {}
    ).catch((err) => {
      this.status = { lastUpdate: null };
    });
  }

  getBatteryNotificationSettingsPromise() {
    return this._get(
      `${this.ingvNewEndpoint()}/admin-tools/user/${
        this.raw.ownerId
      }/device/ref.device.${this.sensor_serial}@${
        this.raw.sensor.series
      }/battery-notification-settings`,
      (data, resolve) => {
        if (data && data.settings) {
          this.batterySettings = data.settings;
        } else {
          this.batterySettings = { error: true };
        }
        resolve(true);
      },
      {}
    ).catch((err) => {
      this.batterySettings = { error: true };
    });
  }

  deleteAssociation() {
    let assocID = this.raw.id;
    let locationID = this.raw.locationId;
    let ownerID = this.raw.ownerId;
    let url = `${this.apiBase()}/user/${ownerID}/location/${locationID}/sensorAssociation/${assocID}`;
    return this._delete(
      url,
      (_data, resolve) => {
        resolve(true);
      },
      {}
    );
  }

  createAssociation(uid) {
    if (this._createPromise) {
      return this._createPromise;
    }
    if (this.device_name.length == 0) {
      this.device_name = "Default";
    }
    let data = {
      name: this.device_name,
      sensorId: this.sensor_identifier,
      locationId: this.location_id,
      ownerId: uid,
    };

    let url = `${this.apiBase()}/user/${uid}/location/${
      this.location_id
    }/sensorAssociations`;
    this._createPromise = this._post(
      url,
      data,
      (_data, resolve) => {
        resolve(true);
      },
      {}
    );
    this._createPromise.then(
      () => {
        this._createPromise = null;
      },
      () => {
        this._createPromise = null;
      }
    );
    return this._createPromise;
  }

  /** Move this association to appear BEFORE the given target association */
  rankBefore(uid: string, targetId: string) {
    const url = `${this.apiBase()}/user/${uid}/location/${this.location_id}/sensorAssociation/${this.identifier}/rank/before/${targetId}`;
    return this._post(url, {}, (_data, resolve) => { resolve(true); }, {});
  }

  /** Move this association to appear AFTER the given target association */
  rankAfter(uid: string, targetId: string) {
    const url = `${this.apiBase()}/user/${uid}/location/${this.location_id}/sensorAssociation/${this.identifier}/rank/after/${targetId}`;
    return this._post(url, {}, (_data, resolve) => { resolve(true); }, {});
  }

  /**
   * Smart reposition: compares weights to pick before vs after.
   */
  reposition(uid: string, targetId: string, targetWeight?: number) {
    if (targetWeight != null) {
      return this.weight > targetWeight
        ? this.rankBefore(uid, targetId)
        : this.rankAfter(uid, targetId);
    }
    return this.rankBefore(uid, targetId);
  }

  editPrep() {
    this.editBackup["name"] = this.device_name;
    if (this.isStation) {
      this.editBackup["timezone"] = this.geo.timezone;
      this.editBackup["pc"] = this.geo.pc;
      this.editBackup["anon"] = this.geo.anonymous;
    }
  }

  cancelEdit() {
    this.device_name = this.editBackup["name"];
    if (this.isStation) {
      this.geo.timezone = this.editBackup["timezone"];
      this.geo.pc = this.editBackup["pc"];
      this.geo.anonymous = this.editBackup["anon"];
    }
  }

  updateAssociation(uid, forceWeight?: number) {
    let data: any = {
      name: this.device_name,
      locationId: Number(this.location_id),
    };
    if (forceWeight !== undefined) {
      data.weight = forceWeight;
    }
    let url = `${this.apiBase()}/user/${uid}/location/${
      this.location_id
    }/sensorAssociation/${this.identifier}`;
    return this._put(
      url,
      data,
      (_data, resolve) => {
        resolve(true);
      },
      {}
    );
  }

  updateGeoLegacy() {
    let data = {
      zip: this.geo.pc,
      timezone: this.geo.timezone,
      anonymous: this.geo.anonymous,
      countryCode: this.geo.countryCode,
    };
    let url = `${this.apiBase()}/sensor/${this.raw.sensorId}/setGeo`;
    return this._put(
      url,
      data,
      (_data, resolve) => {
        resolve(true);
      },
      {}
    );
  }

  searchGeoCode() {
    let url = `${this.ingvEndpoint()}/forecast/client/cityorpc/${
      this.geo.pc
    }/cc/${this.geo.countryCode}`;

    return this._get(url, (data, resolve) => {
      resolve(data);
    });
    /*  //Sample locations for testing
    return new Promise((resolve, reject) => {
      resolve({"locations":[
        {"city_name":"New York","country_code":"US","acw_key":"3697_PC","administrative_area":"NY","tz_name":"America/New_York"},
        {"city_name":"Beverly Hills","country_code":"US","acw_key":"37935_PC","administrative_area":"NY","tz_name":"America/New_York"}
      ]})
    });
    */
  }
  assignGeo(key, ccode, tz) {
    if (key == "") {
      this.geo.pc = "";
      ccode = "";
    }

    const oldData = {
      city_or_postal_code: this.geo.pc,
      acw_key: key,
      country_code: ccode,
    };
    const oldUrl = `${this.ingvEndpoint()}/forecast/admin/location/device/${
      this.sensor_serial
    }`;

    const newData = {
      zip: this.geo.pc,
      countryCode: ccode,
      timezone: tz,
      anonymous: false,
    };
    const newUrl = `${this.apiBase()}/sensor/${this.sensor_identifier}/setGeo`;

    return new Promise((resolve, reject) => {
      this._post(
        oldUrl,
        oldData,
        () => {
          this._put(
            newUrl,
            newData,
            (data) => resolve(data),
            (err) => reject(err)
          );
        },
        (err) => reject(err)
      );
    });
  }

  widget_type() {
    return WidgetEnum.EMBED_WIDGET__USER_DEVICE_ASSOCIATION_ENTITY;
  }
} // end Package
